import React, { useState } from 'react';
import { useStartupWizard } from '@/contexts/StartupWizardContext';
import { CheckCircle, X, Monitor, Image, Link } from 'lucide-react';

const StartupWizard = () => {
  const { showWizard, completeWizard, hideWizard } = useStartupWizard();
  const [currentStep, setCurrentStep] = useState(1);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!showWizard) return null;

  const steps = [
    {
      id: 1,
      title: "Download App & Setup Device",
      description: "Download the MyDigiSign.AI app from your device's app store and complete the device setup process.",
      icon: <Monitor className="w-16 h-16 text-blue-600" />,
      details: [
        "Download MyDigiSign.AI from Google Play, Amazon Appstore, LG Content Store, or Samsung App Store",
        "Install and launch the app",
        "Follow the device registration steps"
      ]
    },
    {
      id: 2,
      title: "Create Screen Content",
      description: "Create engaging content for your MyDigiSign.AI digital signage displays.",
      icon: <Image className="w-16 h-16 text-blue-600" />,
      details: [
        "Go to Screens section in dashboard",
        "Choose content type (image, video, slideshow)",
        "Create stunning AI-generated content instantly",
        "Upload your media files"
      ]
    },
    {
      id: 3,
      title: "Assign Screen to Device",
      description: "Connect your content to your MyDigiSign.AI physical display device.",
      icon: <Link className="w-16 h-16 text-blue-600" />,
      details: [
        "Navigate to Devices section",
        "Select your registered device",
        "Assign a screen or channel",
        "Test the connection and display"
      ]
    }
  ];

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Only the checkbox can prevent future shows
    completeWizard(dontShowAgain);
  };

  const handleSkip = () => {
    // Skip just closes the wizard for now, doesn't prevent future shows
    hideWizard();
  };

  const currentStepData = steps.find(step => step.id === currentStep);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Welcome to MyDigiSign.AI!</h1>
              <p className="text-blue-100 mt-1">Let's get you started in 3 easy steps</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-blue-100 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  step.id <= currentStep 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {step.id < currentStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    step.id
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    step.id < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-sm text-gray-600 text-center">
            Step {currentStep} of {steps.length}
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">{currentStepData.icon}</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {currentStepData.title}
            </h2>
            <p className="text-gray-600 text-lg">
              {currentStepData.description}
            </p>
          </div>

          {/* Step Details */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">What you'll do:</h3>
            <ul className="space-y-3">
              {currentStepData.details.map((detail, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{detail}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Don't Show Again Option */}
          <div className="flex items-center justify-center mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-gray-600">
                Don't show this wizard again
              </span>
            </label>
          </div>
        </div>

        {/* Navigation */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>

          <div className="flex space-x-3">
            <button
              onClick={handleSkip}
              className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Skip
            </button>
            
            {currentStep === 3 ? (
              <button
                onClick={handleComplete}
                className="px-8 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Get Started!
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupWizard;
